
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Cpu, Volume2, Settings, Zap, Network, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const FeaturesAndBenefitsAudio = () => {
  const { t } = useLanguage();

  const keyFeatures = [
    {
      icon: <Cpu className="h-8 w-8 text-[#069E50]" />,
      title: "High-Performance Audio Processing",
      description: "Powered by the ADI 450MHz SHARC 21489 DSP chip delivering 24-bit / 48kHz professional-grade audio"
    },
    {
      icon: <Volume2 className="h-8 w-8 text-[#069E50]" />,
      title: "Flexible I/O Configurations",
      description: "Multiple combinations of balanced analog inputs/outputs with Dante-enabled digital audio networking"
    },
    {
      icon: <Settings className="h-8 w-8 text-[#069E50]" />,
      title: "Advanced DSP Algorithms",
      description: "Comprehensive suite including AFC, AEC, ANS, ANC, AGC, Gain Sharing AutoMix, and AMC processing"
    },
    {
      icon: <Zap className="h-8 w-8 text-[#069E50]" />,
      title: "Audio Tools Per Channel",
      description: "Graphic and parametric EQ, dynamics processing, ducker and delay functions for precise control"
    },
    {
      icon: <Network className="h-8 w-8 text-[#069E50]" />,
      title: "Control & Configuration",
      description: "Configurable GUI, preset memory slots, and DSP Configurator software for easy setup and tuning"
    },
    {
      icon: <Users className="h-8 w-8 text-[#069E50]" />,
      title: "Seamless Integration",
      description: "Designed to integrate seamlessly with Aurora Video over IP systems for complete AV solutions"
    }
  ];

  const audioProcessing = [
    {
      title: "DSP Chip Technology",
      items: [
        "ADI 450MHz SHARC 21489 DSP chip for superior processing power",
        "24-bit / 48kHz professional-grade audio quality",
        "High-performance real-time audio processing capabilities",
        "Low-latency operation for live audio applications"
      ]
    },
    {
      title: "I/O Connectivity",
      items: [
        "Multiple combinations of balanced analog inputs and outputs",
        "Dante-enabled digital audio networking capabilities",
        "GPI/O multi-function ports for comprehensive system integration",
        "USB interface for digital audio playback and recording (model-dependent)"
      ]
    }
  ];

  const dspAlgorithms = [
    {
      title: "Adaptive Audio Control",
      items: [
        "AFC – Adaptive Feedback Control for automatic feedback suppression",
        "AEC – Acoustic Echo Cancellation for clear communication",
        "ANS – Automatic Noise Suppression for clean audio",
        "ANC – Ambient Noise Compensation for consistent levels"
      ]
    },
    {
      title: "Audio Management",
      items: [
        "AGC – Automatic Gain Control for consistent audio levels",
        "Gain Sharing AutoMix for intelligent microphone mixing",
        "AMC – Automatic Mixture Control (select models)",
        "Advanced processing algorithms for professional audio quality"
      ]
    }
  ];

  const audioTools = [
    {
      title: "Equalization & Dynamics",
      items: [
        "Graphic EQ for frequency shaping and room correction",
        "Parametric EQ for precise frequency control",
        "Dynamics processing with compressor and limiter functions",
        "Professional-grade audio processing tools"
      ]
    },
    {
      title: "Timing & Control",
      items: [
        "Ducker functions for automatic level control",
        "Delay functions for time alignment and synchronization",
        "Per-channel processing for individual audio management",
        "Real-time parameter adjustment capabilities"
      ]
    }
  ];

  const controlConfiguration = [
    {
      title: "Software Control",
      items: [
        "Configurable GUI for managing single or multiple units",
        "DSP Configurator software for easy setup and tuning",
        "Intuitive interface for real-time parameter adjustment",
        "Remote control capabilities for system management"
      ]
    },
    {
      title: "Memory & Presets",
      items: [
        "Preset memory slots for storing system configurations",
        "Quick recall of optimized settings for different scenarios",
        "Configuration backup and restore capabilities",
        "Scalable management for multiple unit installations"
      ]
    }
  ];

  const applications = [
    {
      title: "Educational & Corporate",
      items: [
        "Education and Lecture Halls with clear audio distribution",
        "Corporate and Conference Environments for professional meetings",
        "Broadcast and AV Installations for media production",
        "Multi-room and Zoned Audio Systems for flexible coverage"
      ]
    },
    {
      title: "Hospitality & Worship",
      items: [
        "Hospitality venues: Bars, Restaurants, Hotels",
        "House of Worship for clear congregation audio",
        "Entertainment venues with professional sound systems",
        "Public address systems for large facilities"
      ]
    }
  ];

  const benefits = [
    "Seamless integration with Aurora Video over IP systems for complete AV solutions",
    "Network-based audio routing minimizes cabling and reduces device overhead",
    "Scalable architecture supports installations from small to large and complex",
    "High-fidelity audio performance ensures optimal sound quality in all environments",
    "Flexible I/O and control options tailored for diverse application requirements",
    "Easy-to-use software tools streamline configuration and ongoing system control",
    "Professional-grade DSP processing delivers superior audio quality",
    "Advanced algorithms automatically optimize audio performance"
  ];

  const handleViewProducts = () => {
    // Scroll to top when navigating
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to="/audio-options"
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back.to.audio')}
        </Link>
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          Aurora DTX Series DSPs - Features & Benefits
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl">
          Discover the powerful features and compelling benefits of Aurora's DTX Series Digital Signal Processors, 
          designed to deliver professional-grade audio processing while seamlessly integrating with Video over IP systems.
        </p>
      </div>

      {/* Key Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Features</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Audio Processing Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">High-Performance Audio Processing</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {audioProcessing.map((processing, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{processing.title}</h3>
              <ul className="space-y-2">
                {processing.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced DSP Algorithms Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Advanced DSP Algorithms</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {dspAlgorithms.map((algorithm, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{algorithm.title}</h3>
              <ul className="space-y-2">
                {algorithm.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Audio Tools Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Audio Tools Per Channel</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {audioTools.map((tool, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{tool.title}</h3>
              <ul className="space-y-2">
                {tool.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Control & Configuration Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Control & Configuration</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {controlConfiguration.map((control, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{control.title}</h3>
              <ul className="space-y-2">
                {control.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Applications Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Typical Applications</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {applications.map((application, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{application.title}</h3>
              <ul className="space-y-2">
                {application.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Business Benefits Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Benefits</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-muted rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Ready to Explore Audio Products?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Browse our complete range of audio solutions including DSPs, amplifiers, and speakers to find the perfect products for your installation.
        </p>
        <Link to="/audio-options" onClick={handleViewProducts}>
          <Button size="lg">
            View Audio Products
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturesAndBenefitsAudio;
